import { STAGE } from '../../design/lab/palette';
import React, { useState } from 'react';
import BlobBot, { BotState } from './BlobBot';

/**
 * The seven states, side by side, running simultaneously.
 *
 * Motion is hard to describe and easy to recognise, so the only honest way to
 * document a motion system is to run it. Each cell states what the agent is
 * doing and what the motion is doing, so the mapping is checkable rather than
 * asserted.
 */
const STATES: { state: BotState; when: string; motion: string }[] = [
    { state: 'idle', when: 'ready for your question', motion: 'gentle breathing and a calm blink' },
    { state: 'curious', when: 'you are drafting', motion: 'attentive tilt and open eyes' },
    { state: 'thinking', when: 'considering your question', motion: 'miniature heads orbit the avatar' },
    { state: 'working', when: 'running a tool', motion: 'focused eyes and a rhythmic pulse' },
    { state: 'waiting', when: 'blocked on something slow', motion: 'patient lean, gaze drifts away' },
    { state: 'blocked', when: 'needs your help', motion: 'brief shudder, then held strain' },
    { state: 'done', when: 'answer delivered', motion: 'a relief bounce and smiling eyes' },
];

const mono: React.CSSProperties = {
    fontFamily: "'JetBrains Mono','Fira Code',monospace",
};

const StateGallery: React.FC = () => {
    const accent = STAGE.green;
    const [hover, setHover] = useState<string | null>(null);

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
                gap: '10px',
                margin: '10px 0 14px',
                padding: '14px 12px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: '10px',
            }}
        >
            {STATES.map(({ state, when, motion }) => (
                <div
                    key={state}
                    onMouseEnter={() => setHover(state)}
                    onMouseLeave={() => setHover(null)}
                    style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: '9px', padding: '10px 6px', borderRadius: '9px',
                        background: hover === state ? 'rgba(255,255,255,0.045)' : 'transparent',
                        transition: 'background 0.2s',
                    }}
                >
                    <BlobBot color={accent} size={46} state={state} />
                    <div style={{ ...mono, fontSize: '11px', color: accent, fontWeight: 700 }}>
                        {state}
                    </div>
                    <div style={{
                        ...mono, fontSize: '9.5px', color: STAGE.muted,
                        textAlign: 'center', lineHeight: 1.45,
                    }}>
                        {when}
                    </div>
                    <div style={{
                        ...mono, fontSize: '9.5px', color: STAGE.muted,
                        textAlign: 'center', lineHeight: 1.45,
                        opacity: 1,
                        transition: 'opacity 0.2s',
                    }}>
                        {motion}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default StateGallery;
